import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { safeStorageGet, safeStorageSet } from './storage'

const STORAGE_KEY = 'ui.lang'

const STRINGS = {
  zh: {
    appTitle: 'Chamii',
    monitor: '控制台',
    story: '故事',
    accounts: '账号',
    commerce: 'Commerce',
    flash: '刷写',
    vibokey: 'Vibokey',
    eyes: 'Eyes',
    access: 'Access',
    platform: '平台',
    language: '语言',
    chinese: '中文',
    english: '英文',
    theme: '主题',
    beta: '测试版',
    chamiiAdmin: 'Chamii 管理台',
    expandSidebar: '展开',
    collapseSidebar: '收起',
    auth: '认证',
    accountSettings: '账号设置',
    email: '邮箱',
    accountId: '账号 ID',
    signedInAs: '当前登录',
    signOut: '退出登录',
    signedOut: '已退出',
    logoutSuccessful: '退出成功。',
    switchToEnglish: 'Switch to English',
    switchToChinese: '切换中文',
    switchToDark: '切换到深色',
    switchToLight: '切换到浅色',
  },
  en: {
    appTitle: 'Chamii',
    monitor: 'Console',
    story: 'Stories',
    accounts: 'Accounts',
    commerce: 'Commerce',
    flash: 'Flash',
    vibokey: 'Vibokey',
    eyes: 'Eyes',
    access: 'Access',
    platform: 'Platform',
    language: 'Language',
    chinese: '中文',
    english: 'EN',
    theme: 'Theme',
    beta: 'BETA',
    chamiiAdmin: 'Chamii Admin',
    expandSidebar: 'Expand',
    collapseSidebar: 'Collapse',
    auth: 'Auth',
    accountSettings: 'Account settings',
    email: 'Email',
    accountId: 'Account ID',
    signedInAs: 'Signed in as',
    signOut: 'Sign out',
    signedOut: 'Signed out',
    logoutSuccessful: 'Logout successful.',
    switchToEnglish: 'Switch to English',
    switchToChinese: '切换中文',
    switchToDark: 'Switch to dark',
    switchToLight: 'Switch to light',
  },
}

function normalizeLang(v) {
  const x = String(v || '').toLowerCase()
  if (x.startsWith('en')) return 'en'
  return 'zh'
}

function formatTemplate(template, params) {
  if (!params || typeof params !== 'object') return template
  return String(template).replace(/\{(\w+)\}/g, (_, key) => (params[key] == null ? `{${key}}` : String(params[key])))
}

function pickLang(lang, entry) {
  if (entry == null) return ''
  if (typeof entry === 'string') return entry
  if (Array.isArray(entry)) return lang === 'zh' ? String(entry[0] ?? '') : String(entry[1] ?? entry[0] ?? '')
  if (typeof entry === 'object') return lang === 'zh' ? String(entry.zh ?? '') : String(entry.en ?? entry.zh ?? '')
  return String(entry)
}

const I18nContext = createContext(null)

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = safeStorageGet(STORAGE_KEY)
    if (saved) return normalizeLang(saved)
    return normalizeLang(navigator.language || 'zh')
  })

  useEffect(() => {
    safeStorageSet(STORAGE_KEY, lang)
  }, [lang])

  const value = useMemo(() => {
    const dict = STRINGS[lang] || STRINGS.zh
    const t = (key, params) => {
      const raw = dict[key] ?? STRINGS.zh[key] ?? key
      return formatTemplate(raw, params)
    }
    const tx = (entry, params) => formatTemplate(pickLang(lang, entry), params)
    return { lang, setLang, t, tx }
  }, [lang])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    return { lang: 'zh', setLang: () => {}, t: (k) => k, tx: (x) => String(x?.zh ?? x?.[0] ?? x ?? '') }
  }
  return ctx
}
